import { useLayoutEffect } from 'react'

import { background } from './tokens'

/** A site's own theme colors, each `#rrggbb`, or `null` to keep the Skillable one. */
export interface SiteThemeColors {
  readonly primary: string | null
  readonly secondary: string | null
}

type Rgb = readonly [number, number, number]

const hexColor = /^#[0-9a-f]{6}$/iu
const white: Rgb = [255, 255, 255]
const black: Rgb = [0, 0, 0]
// The tokens' own contrast texts, for a label on the site's color.
const lightText = '#FFFFFF'
const darkText = '#141414'

function parse(value: string): Rgb {
  return [1, 3, 5].map((start) => Number.parseInt(value.slice(start, start + 2), 16)) as unknown as Rgb
}

function mix(color: Rgb, toward: Rgb, amount: number): Rgb {
  return color.map((channel, index) => Math.round(channel + ((toward[index] ?? 0) - channel) * amount)) as unknown as Rgb
}

function hex(color: Rgb): string {
  return `#${color.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
}

function channels(color: Rgb): string {
  return color.join(' ')
}

// WCAG 2.x relative luminance.
function luminance(color: Rgb): number {
  const [r, g, b] = color.map((channel) => {
    const value = channel / 255
    return value <= 0.039_28 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }) as unknown as Rgb
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrast(a: Rgb, b: Rgb): number {
  const [light, dark] = [luminance(a), luminance(b)].toSorted((x, y) => y - x) as [number, number]
  return (light + 0.05) / (dark + 0.05)
}

/** WCAG AA for text. */
const textContrast = 4.5

/**
 * The tokens' own label colors when one reads at 4.5:1 on `color`; otherwise pure white or black,
 * one of which always does.
 */
function contrastText(color: Rgb): string {
  const tokens = [lightText, darkText].toSorted((a, b) => contrast(color, parse(b)) - contrast(color, parse(a)))
  const best = tokens[0] ?? lightText
  if (contrast(color, parse(best)) >= textContrast) {
    return best
  }
  return contrast(color, white) >= contrast(color, black) ? '#FFFFFF' : '#000000'
}

/**
 * The site's color as the scheme shows it, with its label: moved toward black on light or white on
 * dark just far enough that it reads at 4.5:1 as text on each surface it labels, as active nav
 * links, text buttons and section lists use it. Nearly every brand color needs no move at all.
 */
export function siteSchemeColor(value: string, scheme: 'light' | 'dark'): { readonly main: string; readonly soft: string; readonly contrastText: string } {
  const color = parse(value)
  const soft = scheme === 'light' ? mix(color, white, 0.88) : mix(color, black, 0.8)
  const surfaces = [background.default, background.paper, background.contrastSm].map((pair) => parse(pair[scheme])).concat([soft])
  const toward = scheme === 'light' ? black : white
  let main = color
  for (let step = 1; step <= 50 && surfaces.some((surface) => contrast(main, surface) < textContrast); step++) {
    main = mix(color, toward, step / 50)
  }
  return { main: hex(main), soft: hex(soft), contrastText: contrastText(main) }
}

/** The custom properties MUI reads a palette color from, as the theme names them (`--tms-palette-*`). */
function paletteProperties(name: 'primary' | 'secondary', value: string, scheme: 'light' | 'dark'): string[] {
  const shown = siteSchemeColor(value, scheme)
  const color = parse(shown.main)
  const text = parse(shown.contrastText)
  // Hover moves away from the label, so a label that reads on the color reads on its hover too.
  const dark = mix(color, contrast(text, black) > contrast(text, white) ? black : white, 0.2)
  const light = mix(color, white, 0.25)
  const prefix = `--tms-palette-${name}`
  const properties = [
    `${prefix}-main: ${shown.main}`,
    `${prefix}-mainChannel: ${channels(color)}`,
    `${prefix}-dark: ${hex(dark)}`,
    `${prefix}-darkChannel: ${channels(dark)}`,
    `${prefix}-light: ${hex(light)}`,
    `${prefix}-lightChannel: ${channels(light)}`,
    `${prefix}-contrastText: ${shown.contrastText}`,
    `${prefix}-contrastTextChannel: ${channels(text)}`,
    `${prefix}-soft: ${shown.soft}`,
  ]
  if (name === 'primary') {
    properties.push(`${prefix}-ring: rgba(${color.join(', ')}, ${scheme === 'light' ? '0.35' : '0.45'})`)
    properties.push(`${prefix}-glow: rgba(${color.join(', ')}, ${scheme === 'light' ? '0.28' : '0.35'})`)
  }
  return properties
}

/**
 * The CSS that puts a site's colors in place of the Skillable primary and secondary, in both
 * schemes. `html:root` outranks the theme's own `:root` and `[data-theme]` rules wherever they sit.
 * Empty when the site keeps both of the Skillable colors, or names one that is not `#rrggbb`.
 */
export function siteThemeCss(colors: SiteThemeColors): string {
  const named = (['primary', 'secondary'] as const).flatMap((name) => {
    const value = colors[name]
    return value !== null && hexColor.test(value) ? [{ name, value }] : []
  })
  if (named.length === 0) {
    return ''
  }
  const rule = (selector: string, scheme: 'light' | 'dark') =>
    `${selector} { ${named.flatMap(({ name, value }) => paletteProperties(name, value, scheme)).join('; ')}; }`
  return [rule('html:root', 'light'), rule('html:root[data-theme="dark"]', 'dark')].join('\n')
}

/** Marks the style element that holds the site's colors. */
export const siteThemeAttribute = 'data-site-theme'

/**
 * Applies a site's theme colors to the whole page for as long as the caller is mounted: a style
 * element first in `<head>`, as its selector outranks the theme's own, so the site's custom CSS,
 * which comes later, can still override it. `null` keeps the Skillable colors.
 */
export function useSiteTheme(colors: SiteThemeColors | null): void {
  const primary = colors?.primary ?? null
  const secondary = colors?.secondary ?? null
  // Before paint, so the page never shows in the Skillable colors first on a branded site.
  useLayoutEffect(() => {
    const css = siteThemeCss({ primary, secondary })
    if (css === '') {
      return undefined
    }
    const style = document.createElement('style')
    style.setAttribute(siteThemeAttribute, '')
    style.textContent = css
    document.head.prepend(style)
    return () => {
      style.remove()
    }
  }, [primary, secondary])
}
